import { getAsRepairDeviceTypeFromModelName } from './asRepairDeviceType';
import { getAsRepairExcelFieldMap } from './asRepairExcelImport';
import { normalizeAsRepairFormConfig } from './asRepairFormConfig';
import { isSpecFileUploadField, isUploadSpecTitle } from './specFiles';

const normalizeText = (value) => String(value ?? '').trim();

const normalizeToken = (value) => (
    normalizeText(value)
        .replace(/[\s·・\-_/.,()[\]{}]/g, '')
        .toLowerCase()
);

/** 현장명 비교 — 공백·기호·「역」 제거 후 동일 또는 포함 관계 */
export const normalizeSiteForAsRepairMatch = (value) => (
    normalizeToken(value).replace(/역/g, '')
);

/** 모델명 비교 — 접두 숫자·기호 제거 */
export const normalizeModelForAsRepairMatch = (value) => (
    normalizeText(value)
        .replace(/[\s·・\-_/.,()[\]{}]/g, '')
        .replace(/^\d+[-_]?/i, '')
        .toLowerCase()
);

export const isAsRepairSiteMatched = (excelSiteName, dbSiteName) => {
    const excelSite = normalizeSiteForAsRepairMatch(excelSiteName);
    const dbSite = normalizeSiteForAsRepairMatch(dbSiteName);
    if (!excelSite || !dbSite) {
        return false;
    }
    if (excelSite === dbSite) {
        return true;
    }
    return excelSite.includes(dbSite) || dbSite.includes(excelSite);
};

export const isAsRepairModelMatched = (excelModelName, dbModelName) => (
    normalizeModelForAsRepairMatch(excelModelName) === normalizeModelForAsRepairMatch(dbModelName)
);

const productMatchesTypeFilter = (product, typeFilters) => {
    if (!typeFilters || (typeFilters.adjuster && typeFilters.controller)) {
        return true;
    }
    const productType = String(product?.type || '').trim().toLowerCase();
    if (productType === 'adjuster') {
        return !!typeFilters.adjuster;
    }
    if (productType === 'controller') {
        return !!typeFilters.controller;
    }
    return true;
};

const narrowCandidatesByDeviceType = (candidates, excelModelName) => {
    const deviceType = getAsRepairDeviceTypeFromModelName(excelModelName);
    if (!deviceType) {
        return candidates;
    }
    const narrowed = candidates.filter(
        (product) => String(product?.type || '').trim().toLowerCase() === deviceType,
    );
    return narrowed.length > 0 ? narrowed : candidates;
};

const getExcelRowSiteAndModel = (row, fieldMap) => {
    const siteField = fieldMap.siteName;
    const modelField = fieldMap.modelName;
    const siteName = siteField?.key ? row[siteField.key] : row.siteName;
    const modelName = modelField?.key ? row[modelField.key] : row.productName;
    return {
        siteName: normalizeText(siteName),
        modelName: normalizeText(modelName),
    };
};

/** 엑셀 행 ↔ 메인 리스트(제품) 매칭 */
export const findProductsMatchingExcelRow = (row, fieldMap, products, { typeFilters } = {}) => {
    const list = Array.isArray(products) ? products : [];
    const { siteName, modelName } = getExcelRowSiteAndModel(row, fieldMap);
    if (!modelName) {
        return [];
    }

    let candidates = list.filter((product) => {
        const dbSite = product?.siteName ?? product?.site_name ?? '';
        const dbModel = product?.name ?? product?.productName ?? product?.product_name ?? '';
        return isAsRepairSiteMatched(siteName, dbSite)
            && isAsRepairModelMatched(modelName, dbModel);
    });

    candidates = candidates.filter((product) => productMatchesTypeFilter(product, typeFilters));
    candidates = narrowCandidatesByDeviceType(candidates, modelName);
    return candidates;
};

const matchFieldLabelToSpecTitle = (fieldLabel, specTitle) => {
    const fl = normalizeToken(fieldLabel);
    const st = normalizeToken(specTitle);
    if (!fl || !st) {
        return false;
    }
    return fl === st;
};

const buildTextSpecMapFromProduct = (product) => {
    const map = new Map();
    (Array.isArray(product?.specs) ? product.specs : []).forEach((spec) => {
        const title = normalizeText(spec?.title);
        if (!title || isUploadSpecTitle(title) || isSpecFileUploadField(spec)) {
            return;
        }
        const details = spec?.details;
        if (details == null || details === '') {
            return;
        }
        const text = String(details).trim();
        if (!text || text.includes('__bnsFile')) {
            return;
        }
        map.set(normalizeToken(title), text);
    });
    return map;
};

/** 매칭된 제품 리스트의 사양 값을 A/S 폼 값에 복사 (엑셀 열 값은 유지) */
export const applyProductSpecsToAsRepairValues = (
    values,
    product,
    formConfig,
    protectedFieldKeys = new Set(),
) => {
    const next = { ...values };
    const specMap = buildTextSpecMapFromProduct(product);
    const fields = normalizeAsRepairFormConfig(formConfig).fields;

    fields.forEach((field) => {
        if (!field?.key || protectedFieldKeys.has(field.key)) {
            return;
        }
        if (field.inputType === 'auto') {
            return;
        }
        const matched = Array.from(specMap.entries()).find(([titleToken]) => (
            matchFieldLabelToSpecTitle(field.label, titleToken)
        ));
        if (matched) {
            next[field.key] = matched[1];
        }
    });

    if (!protectedFieldKeys.has('siteName')) {
        const site = normalizeText(product?.siteName ?? product?.site_name);
        if (site) {
            next.siteName = site;
        }
    }
    if (!protectedFieldKeys.has('productName')) {
        const model = normalizeText(product?.name ?? product?.productName ?? product?.product_name);
        if (model) {
            next.productName = model;
        }
    }

    return next;
};

/**
 * 파싱된 엑셀 행 → DB 리스트 매칭 후 사양 복사
 * @returns {{ rows: object[], unregistered: object[], ambiguous: object[], skippedMatch: number }}
 */
export const mergeExcelRowsWithMatchedProducts = (
    parsedRows,
    formConfig,
    products,
    { typeFilters, managementScope } = {},
) => {
    const rows = Array.isArray(parsedRows) ? parsedRows : [];
    const fieldMap = getAsRepairExcelFieldMap(formConfig, managementScope);
    const protectedFieldKeys = new Set(
        Object.values(fieldMap)
            .filter(Boolean)
            .map((field) => field.key),
    );

    const matchedRows = [];
    const unregistered = [];
    const ambiguous = [];

    rows.forEach((row, index) => {
        const { siteName, modelName } = getExcelRowSiteAndModel(row, fieldMap);
        const candidates = findProductsMatchingExcelRow(row, fieldMap, products, { typeFilters });

        if (candidates.length === 0) {
            unregistered.push({
                rowNumber: index + 1,
                siteName,
                modelName,
            });
            return;
        }
        if (candidates.length > 1) {
            ambiguous.push({
                rowNumber: index + 1,
                siteName,
                modelName,
                candidateCount: candidates.length,
            });
            return;
        }

        matchedRows.push(
            applyProductSpecsToAsRepairValues(row, candidates[0], formConfig, protectedFieldKeys),
        );
    });

    return {
        rows: matchedRows,
        unregistered,
        ambiguous,
        skippedMatch: unregistered.length + ambiguous.length,
    };
};
